/**
 * Servicio para obtener tasas de cambio USDT multi-moneda en tiempo real
 * Soporta EUR, USD, GBP, ARS, MXN, COP, BRL, CLP, PEN, UYU
 * 
 * REFACTOR: Llama al backend Flask en lugar de APIs externas directamente
 * para evitar problemas de CORS y rate limiting desde el navegador.
 */

// Caché de tasas con TTL (por moneda) - cache del lado del cliente
const rateCache: Record<string, { value: number; timestamp: number }> = {};
const CACHE_TTL = 5 * 60 * 1000; // 5 minutos TTL

// Tasas de fallback por moneda (última defensa si el backend falla)
const FALLBACK_RATES: Record<string, number> = {
  EUR: 0.92,
  USD: 1.0,   // USDT = USD (1:1)
  GBP: 0.79,
  ARS: 950.0,  // Argentine Peso
  MXN: 17.5,   // Mexican Peso
  COP: 4000.0, // Colombian Peso
  BRL: 5.0,    // Brazilian Real
  CLP: 900.0,  // Chilean Peso
  PEN: 3.7,    // Peruvian Sol
  UYU: 40.0,   // Uruguayan Peso
  TRY: 34.0,   // Turkish Lira
};

// Monedas soportadas
const SUPPORTED_CURRENCIES = Object.keys(FALLBACK_RATES);

// Backend API URL
// SIEMPRE usar el proxy de Next.js para evitar CORS y simplificar configuración
// El proxy en /pages/api/exchange-rate/[currency].ts maneja la comunicación con Flask
const USE_NEXTJS_PROXY = true;

/**
 * Obtener tasa de cambio USDT a cualquier moneda soportada
 * Llama al backend Flask que maneja la lógica de APIs externas
 * 
 * @param targetCurrency - Código de moneda (EUR, USD, GBP, ARS, MXN, COP, BRL, CLP, PEN, UYU)
 * @returns Tasa de cambio (e.g., 0.92 para EUR significa 1 USDT = 0.92 EUR)
 */
export const getExchangeRate = async (targetCurrency: string = 'EUR'): Promise<number> => {
  targetCurrency = targetCurrency.toUpperCase();
  
  // Validar moneda soportada
  if (!SUPPORTED_CURRENCIES.includes(targetCurrency)) {
    console.warn(`⚠️ [ExchangeRate] Unsupported currency: ${targetCurrency}, defaulting to EUR`);
    targetCurrency = 'EUR';
  }
  
  // USD siempre es 1:1 con USDT
  if (targetCurrency === 'USD') {
    return 1.0;
  }
  
  // Verificar caché del lado del cliente
  const cached = rateCache[targetCurrency];
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    const age = Math.round((Date.now() - cached.timestamp) / 1000);
    console.log(`💰 [ExchangeRate] Using cached ${targetCurrency} rate: ${cached.value} (age: ${age}s)`);
    return cached.value;
  }

  console.log(`🔄 [ExchangeRate] Cache expired for ${targetCurrency}, fetching from backend...`);

  try {
    // Usar siempre el proxy de Next.js
    const url = `/api/exchange-rate/${targetCurrency}`;
    
    console.log(`🌐 [ExchangeRate] Fetching via Next.js proxy: ${url}`);

    // Llamar al backend (Flask directo o proxy Next.js)
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
      signal: AbortSignal.timeout(8000),
    });

    if (!response.ok) {
      throw new Error(`Backend returned ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    
    if (data.success && data.rate && !isNaN(data.rate) && data.rate > 0) {
      // Actualizar caché del cliente
      rateCache[targetCurrency] = {
        value: data.rate,
        timestamp: Date.now()
      };
      
      console.log(`✅ [ExchangeRate] Got ${targetCurrency} rate from backend: ${data.rate}`);
      return data.rate;
    } else {
      throw new Error(`Invalid rate in backend response: ${data.rate}`);
    }
    
  } catch (error) {
    console.error(`❌ [ExchangeRate] Backend call failed:`, error instanceof Error ? error.message : error);
    
    // Si el backend falla, usar caché stale si existe (24h)
    if (cached && Date.now() - cached.timestamp < 24 * 60 * 60 * 1000) {
      const ageMin = Math.round((Date.now() - cached.timestamp) / 1000 / 60);
      console.warn(`⚠️ [ExchangeRate] Using stale cache for ${targetCurrency}: ${cached.value} (age: ${ageMin}min)`);
      return cached.value;
    }

    // Último recurso: tasa de fallback
    const fallback = FALLBACK_RATES[targetCurrency] || FALLBACK_RATES.EUR;
    console.error(`❌ [ExchangeRate] Using fallback rate for ${targetCurrency}: ${fallback}`);
    return fallback;
  }
};

/**
 * Obtener tasa USDT/EUR (backward compatibility)
 */
export const getUsdtToEurRate = async (): Promise<number> => {
  return getExchangeRate('EUR');
};

/**
 * Calcular equivalencia desde USDT a cualquier moneda
 */
export const convertFromUsdt = async (usdtAmount: number, targetCurrency: string): Promise<number> => {
  try {
    const rate = await getExchangeRate(targetCurrency);
    return usdtAmount * rate;
  } catch (error) {
    console.error(`Error converting USDT to ${targetCurrency}:`, error);
    const fallback = FALLBACK_RATES[targetCurrency.toUpperCase()] || FALLBACK_RATES.EUR;
    return usdtAmount * fallback;
  }
};

/**
 * Calcular balance en EUR desde USDT (backward compatibility)
 */
export const calculateEurFromUsdt = async (usdtAmount: number): Promise<number> => {
  return convertFromUsdt(usdtAmount, 'EUR');
};

/**
 * Formatear balance con tasas en vivo para moneda específica
 */
export const calculateBalances = async (
  balanceData: { balance: number }, 
  displayCurrency: string = 'EUR'
): Promise<{ usdt: string, display: string, displayCurrency: string, rate: number }> => {
  const usdtBalance = balanceData.balance;
  const rate = await getExchangeRate(displayCurrency);
  const displayBalance = usdtBalance * rate;

  return {
    usdt: usdtBalance.toFixed(2),
    display: displayBalance.toFixed(2),
    displayCurrency: displayCurrency.toUpperCase(),
    rate: rate
  };
};

/**
 * Obtener información de caché para debugging
 */
export const getCacheInfo = (currency: string = 'EUR') => {
  const cached = rateCache[currency.toUpperCase()];
  if (!cached) {
    return {
      currency: currency.toUpperCase(),
      cached: false,
      fallback: FALLBACK_RATES[currency.toUpperCase()] || FALLBACK_RATES.EUR
    };
  }
  
  return {
    currency: currency.toUpperCase(),
    rate: cached.value,
    timestamp: new Date(cached.timestamp).toISOString(),
    age: Math.round((Date.now() - cached.timestamp) / 1000),
    ttl: CACHE_TTL,
    isValid: Date.now() - cached.timestamp < CACHE_TTL,
  };
};

/**
 * Obtener símbolo de moneda
 */
export const getCurrencySymbol = (currency: string): string => {
  const symbols: Record<string, string> = {
    EUR: '€',
    USD: '$',
    GBP: '£',
    ARS: '$',
    MXN: '$',
    COP: '$',
    BRL: 'R$',
    CLP: '$',
    PEN: 'S/',
    UYU: '$U',
    TRY: '₺'
  };
  return symbols[currency.toUpperCase()] || currency.toUpperCase();
};